import React, { useMemo } from 'react';
import { Flex, IconButton, Text } from '@vibe/core';
import { Add, Delete, Edit } from '@vibe/icons';
import { format, parseISO, eachDayOfInterval, isWeekend } from 'date-fns';
import { formatHoursMinutes } from '../../utils/timesheet-capacity';

// ─── Types ─────────────────────────────────────────────────────────────────────

export interface WorklogRow {
  id: string;
  date: string;
  item_name?: string;
  board_name?: string;
  group_name?: string;
  monday_item_id: number;
  monday_user_id: string;
  user_name?: string;
  hours: number;
  description?: string;
  createdByUserId?: string | null;
}

export interface TimeTrackTableProps {
  /** Array of worklog entries to display */
  worklogs: WorklogRow[];
  /** Callback when the user clicks the edit action */
  onEdit: (id: string) => void;
  /** Callback when the user clicks the delete action */
  onDelete: (id: string) => void;
  /** Whether the table is in a loading state */
  isLoading?: boolean;
  /** Start date of the visible range (yyyy-MM-dd) */
  dateRangeStart: string;
  /** End date of the visible range (yyyy-MM-dd) */
  dateRangeEnd: string;
  /** Callback to add a worklog on a specific date for a specific user */
  onAddWorklogForCell?: (userId: string, date: string) => void;
  /** Callback when a cell is clicked to show worklog list popup */
  onCellClick?: (userId: string, date: string, worklogs: WorklogRow[]) => void;
  /** When true, hides all edit/delete/add controls (period is locked) */
  readOnly?: boolean;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

interface UserRow {
  userId: string;
  userName: string;
  /** Map of date (yyyy-MM-dd) → worklogs for that cell */
  dayMap: Map<string, WorklogRow[]>;
  /** Total hours across all days */
  totalHours: number;
  /** Total worklog count */
  totalCount: number;
}

function buildDayColumns(start: string, end: string): string[] {
  const startDate = parseISO(start);
  const endDate = parseISO(end);
  if (startDate > endDate) return [];
  return eachDayOfInterval({ start: startDate, end: endDate }).map((d) =>
    format(d, 'yyyy-MM-dd'),
  );
}

function buildUserRows(worklogs: WorklogRow[]): UserRow[] {
  const userMap = new Map<string, UserRow>();

  for (const w of worklogs) {
    const userId = w.monday_user_id;
    let row = userMap.get(userId);
    if (!row) {
      row = {
        userId,
        userName: w.user_name || (userId && userId !== 'undefined' && userId !== '' ? `User #${userId}` : 'Unknown user'),
        dayMap: new Map(),
        totalHours: 0,
        totalCount: 0,
      };
      userMap.set(userId, row);
    }

    const dateKey = w.date.slice(0, 10);
    const existing = row.dayMap.get(dateKey);
    if (existing) {
      existing.push(w);
    } else {
      row.dayMap.set(dateKey, [w]);
    }
    row.totalHours += Number(w.hours) || 0;
    row.totalCount += 1;
  }

  // Sort users alphabetically by name
  const rows = Array.from(userMap.values());
  rows.sort((a, b) => a.userName.localeCompare(b.userName));
  return rows;
}


// ─── Component ──────────────────────────────────────────────────────────────

/**
 * Matrix / Grid view for the Time Tracking workspace.
 *
 * Displays a users × days grid. Each row represents a user, each column
 * represents a day in the current date range. Cells show total hours logged
 * and a "+" button to add new worklogs.
 *
 * Reference: TeamBoard documentation pages 78-79 showing the matrix layout.
 */
const TimeTrackTable: React.FC<TimeTrackTableProps> = ({
  worklogs,
  onEdit,
  onDelete,
  isLoading = false,
  dateRangeStart,
  dateRangeEnd,
  onAddWorklogForCell,
  onCellClick,
  readOnly = false,
}) => {
  const days = useMemo(
    () => buildDayColumns(dateRangeStart, dateRangeEnd),
    [dateRangeStart, dateRangeEnd],
  );

  const userRows = useMemo(() => buildUserRows(worklogs), [worklogs]);

  // Compute per-day totals across all users
  const dayTotals = useMemo(() => {
    const totals = new Map<string, { hours: number; count: number }>();
    for (const day of days) {
      totals.set(day, { hours: 0, count: 0 });
    }
    for (const w of worklogs) {
      const dateKey = w.date.slice(0, 10);
      const t = totals.get(dateKey);
      if (t) {
        t.hours += Number(w.hours) || 0;
        t.count += 1;
      }
    }
    return totals;
  }, [days, worklogs]);

  // Grand total
  const grandTotal = useMemo(
    () => worklogs.reduce((sum, w) => sum + (Number(w.hours) || 0), 0),
    [worklogs],
  );

  const grandCount = worklogs.length;

  return (
    <div className="rp-tt-matrix">
      {/* Summary bar */}
      <div className="rp-tt-matrix__summary">
        <Text type={Text.types.TEXT2} style={{ color: '#676879' }}>
          {grandCount} {grandCount === 1 ? 'worklog' : 'worklogs'}
        </Text>
        <Text type={Text.types.TEXT1} weight={Text.weights.BOLD}>
          {formatHoursMinutes(grandTotal)} total
        </Text>
      </div>

      {/* Scrollable grid container */}
      <div className="rp-tt-matrix__scroll">
        <table className="rp-tt-matrix__table">
          {/* ── Column header: days ──────────────────────────────── */}
          <thead>
            <tr>
              <th className="rp-tt-matrix__th rp-tt-matrix__th--user">
                User
              </th>
              {days.map((day) => {
                const dt = parseISO(day);
                const isWknd = isWeekend(dt);
                const dayTotal = dayTotals.get(day);
                return (
                  <th
                    key={day}
                    className={`rp-tt-matrix__th rp-tt-matrix__th--day ${
                      isWknd ? 'rp-tt-matrix__th--weekend' : ''
                    }`}
                  >
                    <div className="rp-tt-matrix__day-header">
                      <span className="rp-tt-matrix__day-name">
                        {format(dt, 'EEE')}
                      </span>
                      <span className="rp-tt-matrix__day-number">
                        {format(dt, 'd')}
                      </span>
                    </div>
                    {dayTotal && dayTotal.hours > 0 && (
                      <span className="rp-tt-matrix__day-total">
                        {formatHoursMinutes(dayTotal.hours)}
                      </span>
                    )}
                  </th>
                );
              })}
              <th className="rp-tt-matrix__th rp-tt-matrix__th--total">
                Total
              </th>
            </tr>
          </thead>

          {/* ── User rows ────────────────────────────────────────── */}
          <tbody>
            {userRows.length === 0 && !isLoading && (
              <tr className="rp-tt-matrix__row">
                <td className="rp-tt-matrix__td rp-tt-matrix__td--user">
                  <Text type={Text.types.TEXT2} style={{ color: '#676879' }}>
                    —
                  </Text>
                </td>
                {days.map((day) => {
                  const isWknd = isWeekend(parseISO(day));
                  return (
                    <td
                      key={day}
                      className={`rp-tt-matrix__td rp-tt-matrix__td--day ${
                        isWknd ? 'rp-tt-matrix__td--weekend' : ''
                      }`}
                    >
                      <div className="rp-tt-matrix__cell">
                        <span className="rp-tt-matrix__cell-empty">—</span>
                        {onAddWorklogForCell && !readOnly && (
                          <button
                            className="rp-tt-matrix__cell-add"
                            onClick={(e) => {
                              e.stopPropagation();
                              onAddWorklogForCell('', day);
                            }}
                            title="Add worklog"
                          >
                            +
                          </button>
                        )}
                      </div>
                    </td>
                  );
                })}
                <td className="rp-tt-matrix__td rp-tt-matrix__td--total">
                  <span className="rp-tt-matrix__row-total">0h</span>
                </td>
              </tr>
            )}
            {userRows.map((row) => (
              <tr key={row.userId} className="rp-tt-matrix__row">
                {/* User cell (Y-axis) */}
                <td className="rp-tt-matrix__td rp-tt-matrix__td--user">
                  <div className="rp-tt-matrix__user-info">
                    <span
                      className="rp-tt-matrix__user-avatar"
                      title={row.userName}
                    >
                      {row.userName.charAt(0).toUpperCase()}
                    </span>
                    <div className="rp-tt-matrix__user-details">
                      <span className="rp-tt-matrix__user-name">
                        {row.userName}
                      </span>
                      <span className="rp-tt-matrix__user-meta">
                        {row.totalCount}{' '}
                        {row.totalCount === 1 ? 'worklog' : 'worklogs'} |{' '}
                        {formatHoursMinutes(row.totalHours)}
                      </span>
                    </div>
                  </div>
                </td>

                {/* Day cells */}
                {days.map((day) => {
                  const isWknd = isWeekend(parseISO(day));
                  const cellWorklogs = row.dayMap.get(day) ?? [];
                  const cellHours = cellWorklogs.reduce(
                    (sum, w) => sum + (Number(w.hours) || 0),
                    0,
                  );

                  return (
                    <td
                      key={day}
                      className={`rp-tt-matrix__td rp-tt-matrix__td--day ${
                        isWknd ? 'rp-tt-matrix__td--weekend' : ''
                      } ${cellHours > 0 ? 'rp-tt-matrix__td--has-data' : ''}`}
                      onClick={() =>
                        cellWorklogs.length > 0 &&
                        onCellClick?.(row.userId, day, cellWorklogs)
                      }
                    >
                      <div className="rp-tt-matrix__cell">
                        {cellHours > 0 ? (
                          <span className="rp-tt-matrix__cell-hours">
                            {formatHoursMinutes(cellHours)}
                          </span>
                        ) : (
                          <span className="rp-tt-matrix__cell-empty">—</span>
                        )}
                        {/* Add button for cell */}
                        {onAddWorklogForCell && !readOnly && (
                          <button
                            className="rp-tt-matrix__cell-add"
                            onClick={(e) => {
                              e.stopPropagation();
                              onAddWorklogForCell(row.userId, day);
                            }}
                            title="Add worklog"
                          >
                            +
                          </button>
                        )}
                      </div>
                    </td>
                  );
                })}

                {/* Row total */}
                <td className="rp-tt-matrix__td rp-tt-matrix__td--total">
                  <span className="rp-tt-matrix__row-total">
                    {formatHoursMinutes(row.totalHours)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>

          {/* ── Footer row: totals per day ────────────────────────── */}
          <tfoot>
            <tr className="rp-tt-matrix__footer-row">
              <td className="rp-tt-matrix__td rp-tt-matrix__td--footer-label">
                <Text
                  type={Text.types.TEXT2}
                  weight={Text.weights.BOLD}
                  style={{ color: '#676879' }}
                >
                  Total
                </Text>
              </td>
              {days.map((day) => {
                const dayTotal = dayTotals.get(day);
                const isWknd = isWeekend(parseISO(day));
                return (
                  <td
                    key={day}
                    className={`rp-tt-matrix__td rp-tt-matrix__td--footer ${
                      isWknd ? 'rp-tt-matrix__td--weekend' : ''
                    }`}
                  >
                    {dayTotal && dayTotal.hours > 0 ? (
                      <span className="rp-tt-matrix__footer-hours">
                        {formatHoursMinutes(dayTotal.hours)}
                      </span>
                    ) : (
                      <span className="rp-tt-matrix__cell-empty">—</span>
                    )}
                  </td>
                );
              })}
              <td className="rp-tt-matrix__td rp-tt-matrix__td--grand-total">
                <span className="rp-tt-matrix__grand-total">
                  {formatHoursMinutes(grandTotal)}
                </span>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default TimeTrackTable;
